import React from "react";
import { X } from "lucide-react";

/**
 * SidebarNav — vertical primary navigation.
 *
 * Replaces the horizontal tab bar at the top of the dashboard. Solves
 * two problems:
 *   1. Discovery — tabs like Research were hidden in horizontal scroll
 *      and students never found them.
 *   2. Friction — every tab switch required visual scanning across a
 *      long horizontal row that overflowed on every screen size.
 *
 * Responsive behavior:
 *   - lg+ (≥1024px): fixed left sidebar, always visible (240px wide).
 *   - <lg: hidden by default; renders as a slide-in drawer with a dim
 *     backdrop when `open` is true. Backdrop tap closes it.
 *
 * Active state: gold left-edge accent + subtle bg. Inactive: muted
 * text with hover affordance.
 *
 * Props:
 *   tabs            — [{ id, label, icon }]
 *   activeTab       — string id of currently active tab
 *   onTabChange     — (id) => void
 *   open            — boolean, mobile drawer open state
 *   onClose         — () => void, called when backdrop is tapped
 */

const SidebarNav = ({ tabs, activeTab, onTabChange, open, onClose }) => {
  const handleTabClick = (id) => {
    onTabChange(id);
    // Auto-close drawer on mobile after a tab is picked. No-op on desktop
    // where `open` doesn't gate visibility.
    if (onClose) onClose();
  };

  return (
    <>
      {/* Backdrop — only renders on mobile when drawer is open */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-[240px] z-50
          bg-arc-paper
          border-r border-arc-line
          flex flex-col
          transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:sticky lg:top-0
        `}
        aria-label="Primary navigation"
      >
        {/* Header strip — close button on mobile */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-arc-line">
          <p className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4">Navigation</p>
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden w-8 h-8 rounded-arc-ctl border border-arc-line bg-arc-paper-2 flex items-center justify-center text-arc-ink-3 hover:text-arc-ink hover:border-arc-line-2 transition-all"
            aria-label="Close navigation"
          >
            <X size={14} />
          </button>
        </div>

        {/* Scrollable tab list */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
          {tabs.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleTabClick(id)}
                className={`
                  group w-full flex items-center gap-3 px-3 py-2.5 rounded-arc-ctl
                  text-sm font-medium transition-all relative
                  ${
                    active
                      ? "bg-arc-card text-arc-ink shadow-arc-sm"
                      : "text-arc-ink-3 hover:text-arc-ink hover:bg-arc-paper-2"
                  }
                `}
              >
                {/* Active-state left accent */}
                <span
                  className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full transition-all ${
                    active ? "bg-arc-accent" : "bg-transparent group-hover:bg-arc-line-2"
                  }`}
                  aria-hidden="true"
                />
                <Icon
                  size={16}
                  strokeWidth={active ? 2.25 : 1.75}
                  className={`flex-shrink-0 ${active ? "text-arc-accent" : ""}`}
                />
                <span className="flex-1 text-left">{label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar footer — branding + legal. Legal links are required
            on every authenticated surface so users can review the
            Privacy Policy and Terms while logged in (not just at
            signup), per standard SaaS compliance practice. */}
        <div className="border-t border-arc-line px-5 py-3 space-y-2">
          <div>
            <p className="text-[10px] text-arc-ink-4 font-semibold uppercase tracking-[0.12em]">
              FIRE Tracker
            </p>
            <p className="text-[10px] text-arc-ink-4 mt-0.5">
              ARCrypto member tools
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-arc-ink-4">
            <a
              href="https://arcrypto.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-arc-ink transition-colors underline-offset-4 hover:underline"
            >
              Privacy
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="https://arcrypto.io/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-arc-ink transition-colors underline-offset-4 hover:underline"
            >
              Terms
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SidebarNav;
